import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer, RefusalCard } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { ibamaCasesHref } from '@/pages/ibama/OverviewPage';
import { fetchIbamaDecisions } from '@/services/ibama';
import type { AnswerOut, IbamaBoardLabels, IbamaReachRow, IbamaReachTable } from '@/services/types';

/**
 * How far IBAMA's ledger cases got through the two instances.
 *
 * The ledger dates a first-instance and a board decision and records no
 * amount or outcome code after either, so this is the ANEEL appeals screen
 * with its waterfall refused on its face. The Decisao Administrativa text
 * is an outcome label of the DEBT written on a minority; it is listed as a
 * vocabulary, never read as a ruling. Groups are alphabetical caseloads.
 */
export function IbamaDecisionsPage() {
  const { t } = useTranslation(['ibama', 'common']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['ibama', 'decisions'],
    queryFn: fetchIbamaDecisions,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('ibama:decisions.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('ibama:decisions.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="ibama:decisions.explainer" />

      <div className="flex justify-end">
        <ExportButton path="/api/v1/ibama/export/decisions" label={t('ibama:decisions.export')} />
      </div>

      <AsOfStrip asOf={data.as_of} />

      <div className="grid gap-4 lg:grid-cols-2">
        <LabelsPanel answer={data.labels} />
        <RefusalCard title={t('ibama:decisions.relief.title')} answer={data.relief} />
      </div>

      <FieldGuide i18nKey="ibama:decisions.guide" level={2} />

      <ReachTable answer={data.by_nature} />
      <ReachTable answer={data.by_uf} />
      <ReachTable answer={data.by_year} />
    </div>
  );
}

function LabelsPanel({ answer }: { answer: AnswerOut<IbamaBoardLabels> }) {
  const { t, i18n } = useTranslation(['ibama']);
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });
  return (
    <section className="rounded-lg border bg-card p-4" data-labels>
      <h2 className="mb-3 text-sm font-semibold text-foreground">
        {t('ibama:decisions.labels.title')}
      </h2>
      <Answer answer={answer} help={t('ibama:decisions.labels.help')}>
        {(v) => (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              {t('ibama:decisions.labels.reach', {
                labelled: number.format(v.with_label),
                cases: number.format(v.ledger_cases),
                share: percent.format(v.labelled_share),
                distinct: number.format(v.distinct),
              })}
            </p>
            <ul className="space-y-1 text-sm">
              {v.labels.map((row) => (
                <li key={row.code} className="flex items-baseline gap-2" data-label={row.code}>
                  <span className="min-w-0 flex-1 truncate text-foreground" title={row.code}>
                    {row.code}
                  </span>
                  <span className="tabular-nums text-foreground">{number.format(row.count)}</span>
                  <span className="w-14 text-right text-xs tabular-nums text-muted-foreground">
                    {percent.format(row.share)}
                  </span>
                </li>
              ))}
              {v.other > 0 ? (
                <li className="flex items-baseline gap-2 text-muted-foreground" data-label="other">
                  <span className="flex-1">{t('ibama:decisions.labels.other')}</span>
                  <span className="tabular-nums">{number.format(v.other)}</span>
                  <span className="w-14 text-right text-xs tabular-nums">
                    {percent.format(v.other / v.with_label)}
                  </span>
                </li>
              ) : null}
            </ul>
          </div>
        )}
      </Answer>
    </section>
  );
}

function ReachTable({ answer }: { answer: AnswerOut<IbamaReachTable> }) {
  const { t, i18n } = useTranslation(['ibama']);
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });
  const kind = answer.value?.kind ?? 'nature';
  const href = (row: IbamaReachRow) =>
    kind === 'nature'
      ? ibamaCasesHref({ nature: row.group })
      : kind === 'uf'
        ? ibamaCasesHref({ uf: row.group })
        : ibamaCasesHref({ year: Number(row.group) });
  const boardWithoutFirst = answer.value?.rows.reduce((n, r) => n + r.board_without_first, 0) ?? 0;

  return (
    <section className="rounded-lg border bg-card p-4" data-reach={kind}>
      <h2 className="mb-3 text-sm font-semibold text-foreground">
        {t(`ibama:decisions.reach.${kind}.title`)}
      </h2>
      <Answer answer={answer} help={t(`ibama:decisions.reach.${kind}.help`)}>
        {(table) => (
          <div className="space-y-2">
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">{t('ibama:decisions.reach.group')}</th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ibama:decisions.reach.cases')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ibama:decisions.reach.firstDecided')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ibama:decisions.reach.boardDated')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ibama:decisions.reach.boardWithoutFirst')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ibama:decisions.reach.labelled')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ibama:decisions.reach.settled')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ibama:decisions.reach.inCourt')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ibama:decisions.reach.writtenOff')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {table.rows.map((row) => (
                    <tr key={row.group} className="border-t" data-group={row.group}>
                      <td className="px-3 py-2 text-foreground">{row.group}</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        <Link
                          to={href(row)}
                          className="text-primary underline-offset-2 hover:underline"
                        >
                          {number.format(row.cases)}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        {number.format(row.first_decided)}
                        <span className="ml-1 text-xs text-muted-foreground">
                          ({percent.format(row.first_decided_share)})
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        {number.format(row.board_dated)}
                        <span className="ml-1 text-xs text-muted-foreground">
                          ({percent.format(row.board_dated_share)})
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {number.format(row.board_without_first)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {number.format(row.board_labelled)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        {number.format(row.settled)}
                        <span className="ml-1 text-xs text-muted-foreground">
                          ({percent.format(row.settled_share)})
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {number.format(row.in_court)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {number.format(row.written_off)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {boardWithoutFirst > 0 ? (
              <p className="text-xs text-muted-foreground" data-board-without-first>
                {t('ibama:decisions.reach.boardWithoutFirstNote', {
                  n: number.format(boardWithoutFirst),
                })}
              </p>
            ) : null}
            {table.suppressed > 0 ? (
              <p className="text-xs text-muted-foreground" data-suppressed>
                {t('ibama:decisions.reach.suppressed', {
                  groups: number.format(table.suppressed),
                  cases: number.format(table.suppressed_cases),
                })}
              </p>
            ) : null}
          </div>
        )}
      </Answer>
    </section>
  );
}
